import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import './VendorStatementIdInput.scss';
import { IconEdit } from '../../../components/Icons';

export type VendorStatementIdInputState = 'display' | 'edit' | 'loading';

interface VendorStatementIdInputProps {
  /** Current state of the input */
  state?: VendorStatementIdInputState;
  /** Value for the ID field */
  value?: string;
  /** Placeholder text for the input */
  placeholder?: string;
  /** Label for the field */
  label?: string;
  /** Whether the field is in a loading state */
  isLoading?: boolean;
  /** Callback when value changes */
  onValueChange?: (value: string) => void;
  /** Callback when edit state changes */
  onStateChange?: (state: VendorStatementIdInputState) => void;
}

/**
 * VendorStatementIdInput component for displaying and editing vendor statement ID
 *
 * Features two modes:
 * - Read mode: Shows value with edit button (when value is provided)
 * - Input mode: Shows input field with save button (when editing or no value)
 */
const VendorStatementIdInput: React.FC<VendorStatementIdInputProps> = ({
  state = 'display',
  value = '',
  placeholder = 'Account Number',
  label = 'ID',
  isLoading = false,
  onValueChange,
  onStateChange,
}) => {
  const [isEditing, setIsEditing] = useState(state === 'edit');
  const [tempValue, setTempValue] = useState(value);

  useEffect(() => {
    setTempValue(value);
  }, [value]);

  const handleEditClick = () => {
    setIsEditing(true);
    setTempValue(value);
    onStateChange?.('edit');
  };

  const handleSaveClick = () => {
    onValueChange?.(tempValue);
    setIsEditing(false);
    onStateChange?.('display');
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setTempValue(value);
    onStateChange?.('display');
  };

  const handleInputChange = (inputValue: string) => {
    setTempValue(inputValue);
  };

  const isLoadingState = state === 'loading' || isLoading;
  const hasValue = value !== undefined && value !== null && value !== '';
  const showEditingState = (state === 'edit' || isEditing) && !isLoadingState;
  const showReadOnlyState = !showEditingState && !isLoadingState && hasValue;

  return (
    <div className={classNames('vendor-statement-id-input', `state-${state}`)}>
      <div className="vendor-statement-field">
        <div className="field-label">{label}</div>
        <div className="field-content">
          {showEditingState ? (
            <>
              <input
                type="text"
                value={tempValue}
                onChange={e => handleInputChange(e.target.value)}
                className="field-input editing"
                placeholder={placeholder}
                autoFocus
              />
              <div className="field-actions">
                <button className="action-button save" onClick={handleSaveClick} type="button">
                  ✓
                </button>
              </div>
            </>
          ) : isLoadingState ? (
            <div className="field-loading">
              <span>Loading ID...</span>
            </div>
          ) : showReadOnlyState ? (
            <>
              <div className="field-display">
                <span className="field-value">{value}</span>
              </div>
              <button className="edit-button" onClick={handleEditClick} type="button">
                <IconEdit />
              </button>
            </>
          ) : (
            <>
              <input
                type="text"
                placeholder={placeholder}
                className="field-input placeholder"
                onClick={handleEditClick}
                readOnly
              />
              <button className="edit-button" onClick={handleEditClick} type="button">
                <IconEdit />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VendorStatementIdInput;
